import type React from 'react';
import { ChatTableSpecDto } from 'src/api/FiretrolApiClient';
import {
  CHART_EMPTY_VALUE,
  formatChartValue,
} from 'src/app/chat/chatChartFormat';
import { formatDate, formatDateTime, formatNumber, parseDate } from 'src/utils';

export type ChatTableSortDirection = 'asc' | 'desc';

export const CHAT_TABLE_COLUMN_MIN_WIDTH = 96;

/** Mantine's default reserves scrollbar space under and beside the table even when no scrollbar shows. */
export const CHAT_TABLE_SCROLL_AREA_PROPS = {
  offsetScrollbars: 'present',
} as const;

export const CHAT_TABLE_NUMERIC_CELL_STYLE: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

const NUMERIC_FORMATS: readonly string[] = [
  'number',
  'currency',
  'hours',
  'percent',
];

export const isNumericTableFormat = (format: string): boolean =>
  NUMERIC_FORMATS.includes(format);

const isMidnight = (date: Date): boolean =>
  date.getHours() === 0 &&
  date.getMinutes() === 0 &&
  date.getSeconds() === 0 &&
  date.getMilliseconds() === 0;

const formatTableDate = (value: string): string => {
  const date = parseDate(value);
  if (!date) return value;
  return isMidnight(date) ? formatDate(date) : formatDateTime(date);
};

/** @registry Format-aware display text for one cell of an AI chat table spec (on-screen table, transcript export, print). */
export const formatTableCell = (value: unknown, format: string): string => {
  if (value === null || value === undefined) return CHART_EMPTY_VALUE;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    return isNumericTableFormat(format)
      ? formatChartValue(value, format)
      : String(value);
  }
  if (typeof value === 'string') {
    return format === 'date' ? formatTableDate(value) : value;
  }
  return String(value);
};

type SortKey = number | string | null;

const sortKey = (value: unknown, format: string): SortKey => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (isNumericTableFormat(format)) {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }
  if (format === 'date' && typeof value === 'string') {
    return parseDate(value)?.getTime() ?? null;
  }
  return String(value);
};

const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const compareKeys = (a: SortKey, b: SortKey): number =>
  typeof a === 'number' && typeof b === 'number'
    ? a - b
    : collator.compare(String(a), String(b));

/** The original indexes of the rows ordered by one column, keeping ties in their original order and missing values last in either direction. */
export const sortTableRowOrder = (
  rows: readonly (readonly unknown[])[],
  columnIndex: number,
  format: string,
  direction: ChatTableSortDirection,
): number[] =>
  rows
    .map((row, index) => ({
      index,
      key: sortKey(row[columnIndex], format),
    }))
    .sort((a, b) => {
      if (a.key === null || b.key === null) {
        if (a.key === b.key) return a.index - b.index;
        return a.key === null ? 1 : -1;
      }
      const order = compareKeys(a.key, b.key);
      if (order === 0) return a.index - b.index;
      return direction === 'asc' ? order : -order;
    })
    .map(({ index }) => index);

export const TABLE_EMPTY_TEXT = 'No rows.';

export const tableTruncationText = (rowCount: number): string =>
  rowCount === 0
    ? 'The result was too large to show here.'
    : `Showing the first ${formatNumber(rowCount)} rows.`;

export interface ITableDataGrid {
  header: string[];
  numeric: boolean[];
  rows: string[][];
}

/** A table spec's shown rows as display strings, one per column, in the server's order. */
export const tableDataGrid = (spec: ChatTableSpecDto): ITableDataGrid => {
  const columns = spec.columns ?? [];
  const rows: readonly (readonly unknown[])[] = spec.rows ?? [];
  return {
    header: columns.map((column) => column.label),
    numeric: columns.map((column) => isNumericTableFormat(column.format)),
    rows: rows.map((row) =>
      columns.map((column, index) =>
        formatTableCell(row[index], column.format),
      ),
    ),
  };
};
